import { useState } from 'react';
import { Mail, MessageSquare, Plus, Send, Smartphone, Trash2 } from 'lucide-react';
import {
  smsSegments,
  type GuestMessage,
  type GuestMessageAudience,
  type GuestMessageChannel,
} from '@peles/shared';
import { EntityModal, EntityList, FilterSelect, StatusBadge } from '../common';
import type { EntityListColumn } from '../common';
import { useDialog } from '../../context/DialogContext';
import { MessageComposer } from '../messaging/MessageComposer';
import {
  useGuestMessages,
  useCreateGuestMessage,
  useUpdateGuestMessage,
  useDeleteGuestMessage,
  useSendGuestMessage,
} from '../../hooks/useInvites';
import { useCan } from '../../hooks/useCan';
import { useEventGuestOptions } from '../../hooks/useEventGuests';

const MESSAGE_STATUS_COLORS: Record<string, string> = {
  DRAFT: '#94a3b8',
  SCHEDULED: '#3b82f6',
  SENDING: '#8b5cf6',
  SENT: '#22c55e',
  FAILED: '#ef4444',
};

const CHANNEL_OPTIONS: { value: GuestMessageChannel; label: string; icon: typeof Mail }[] = [
  { value: 'EMAIL', label: 'Email', icon: Mail },
  { value: 'SMS', label: 'SMS', icon: Smartphone },
];

const RSVP_AUDIENCE_OPTIONS = [
  { value: 'invited', label: 'Pending' },
  { value: 'confirmed', label: 'Accepted' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'declined', label: 'Declined' },
];

/** What a text's RSVP link adds: a new line, then `<site>/rsvp/<64-character token>`. */
function rsvpLinkSample(): string {
  return `\n${window.location.origin}/rsvp/${'0'.repeat(64)}`;
}

interface MessagesSectionProps {
  eventId: string;
  /**
   * The client portal: broadcasts go by email only (texts are a paid channel
   * the office runs), and the office's writing assistant is not offered.
   */
  clientMode?: boolean;
}

/**
 * Guest broadcasts ("message all pending guests") for an event. Owns its
 * compose/edit modal (list-components-own-their-modals rule).
 */
export function MessagesSection({ eventId, clientMode = false }: MessagesSectionProps) {
  const { alert, confirm } = useDialog();
  const { data: messages = [], isLoading } = useGuestMessages(eventId);
  const createMessage = useCreateGuestMessage();
  const updateMessage = useUpdateGuestMessage();
  const deleteMessage = useDeleteGuestMessage();
  const sendMessage = useSendGuestMessage();
  const canSendToGuests = useCan('messages.guests');
  const canText = useCan('messages.sms') && !clientMode;
  /** A drafted text stays a text for someone who may not send one — it is only never sent. */
  const maySend = (m: GuestMessage) => canSendToGuests && (m.channel !== 'SMS' || canText);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<GuestMessage | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [channel, setChannel] = useState<GuestMessageChannel>('EMAIL');
  const [rsvpStatuses, setRsvpStatuses] = useState<string[]>([]);
  const [sides, setSides] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const { data: guestOptions } = useEventGuestOptions(eventId);
  const sideOptions = guestOptions?.sides ?? [];
  const isSmsDraft = channel === 'SMS';

  const openCompose = (message?: GuestMessage) => {
    setEditing(message ?? null);
    const sms = message?.channel === 'SMS';
    // A text has no subject line: one stored on an older draft is the text's
    // first line, so it is shown there rather than sent unseen.
    setSubject(sms ? '' : (message?.subject ?? ''));
    setBody(
      sms && message?.subject ? `${message.subject}\n${message.body}` : (message?.body ?? ''),
    );
    setChannel(sms ? 'SMS' : 'EMAIL');
    setRsvpStatuses(message?.audience?.rsvpStatuses ?? []);
    setSides(message?.audience?.sides ?? []);
    setModalOpen(true);
  };

  const toggleStatus = (value: string) => {
    setRsvpStatuses((current) =>
      current.includes(value) ? current.filter((s) => s !== value) : [...current, value],
    );
  };

  const handleSave = async () => {
    if (!body.trim()) {
      await alert({ message: 'Please write a message.', variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const audience: GuestMessageAudience = {
        ...(rsvpStatuses.length ? { rsvpStatuses } : {}),
        ...(sides.length ? { sides } : {}),
      };
      const sentSubject = isSmsDraft ? '' : subject.trim();
      if (editing) {
        await updateMessage.mutateAsync({
          id: editing.id,
          updates: { channel, subject: sentSubject || null, body: body.trim(), audience },
        });
      } else {
        await createMessage.mutateAsync({
          eventId,
          channel,
          subject: sentSubject || undefined,
          body: body.trim(),
          audience,
        });
      }
      setModalOpen(false);
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to save message',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (message: GuestMessage) => {
    const statusLabel = message.audience?.rsvpStatuses?.length
      ? RSVP_AUDIENCE_OPTIONS.filter((o) => message.audience.rsvpStatuses!.includes(o.value))
          .map((o) => o.label)
          .join(' + ')
      : '';
    const sideLabel = message.audience?.sides?.length
      ? `${message.audience.sides
          .map((side) => sideOptions.find((o) => o.value === side)?.label ?? side)
          .join(' + ')} side`
      : '';
    const audienceLabel =
      [statusLabel, sideLabel].filter(Boolean).join(' guests on the ') || 'all guests';
    const isSms = message.channel === 'SMS';
    const ok = await confirm({
      message: `Send this ${isSms ? 'SMS' : 'message'} to ${audienceLabel}?`,
      confirmLabel: 'Send',
    });
    if (!ok) return;
    try {
      const result = await sendMessage.mutateAsync(message.id);
      // Nothing actually went out: the channel is switched off. Saying
      // "reached N guests" here would be a lie the producer acts on.
      if (result.sent === 0 && result.simulated > 0) {
        await alert({
          message:
            `${isSms ? 'SMS' : 'Email'} delivery is turned off, so ${result.simulated} ` +
            `${isSms ? 'text' : 'message'}${result.simulated === 1 ? ' was' : 's were'} ` +
            'simulated, not sent. The message is still unsent — send it again once the ' +
            'channel is enabled.',
          variant: 'warning',
        });
        return;
      }
      await alert({
        message:
          `Reached ${result.sent} guest${result.sent === 1 ? '' : 's'} (${result.emailsSent} ${
            isSms ? 'texts' : 'emails'
          })` +
          (result.failed ? `, ${result.failed} failed` : '') +
          (result.skipped ? `, ${result.skipped} without ${isSms ? 'a phone' : 'email'}` : '') +
          (result.simulated ? `, ${result.simulated} simulated` : '') +
          '.',
        variant: result.failed ? 'warning' : 'success',
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to send message',
        variant: 'error',
      });
    }
  };

  const handleDelete = async (message: GuestMessage) => {
    const ok = await confirm({
      message: 'Delete this message?',
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await deleteMessage.mutateAsync(message.id);
  };

  const columns: EntityListColumn<GuestMessage>[] = [
    {
      key: 'channel',
      header: '',
      render: (m) =>
        m.channel === 'SMS' ? (
          <Smartphone className="w-4 h-4 text-gray-400" aria-label="SMS" />
        ) : (
          <Mail className="w-4 h-4 text-gray-400" aria-label="Email" />
        ),
    },
    {
      key: 'message',
      header: 'Message',
      render: (m) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">
            {m.subject || (m.channel === 'SMS' ? 'Text message' : '(no subject)')}
          </div>
          <div className="text-xs text-gray-500 truncate">{m.body}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (m) => <StatusBadge label={m.status} color={MESSAGE_STATUS_COLORS[m.status]} />,
    },
    {
      key: 'reached',
      header: 'Reached',
      align: 'center',
      hideOnMobile: true,
      render: (m) => <span>{m.recipientCount ?? 0}</span>,
    },
    {
      key: 'by',
      header: 'By',
      hideOnMobile: true,
      render: (m) => (
        <span className="text-xs text-gray-600">{m.sentByName ?? m.createdByName ?? '—'}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (m) => (
        <div className="flex items-center justify-end gap-1">
          {m.status !== 'SENT' && maySend(m) && (
            <button
              type="button"
              aria-label={`Send message ${m.subject || m.id}`}
              onClick={(e) => {
                e.stopPropagation();
                void handleSend(m);
              }}
              className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
          {/* A client withdraws only what has not gone out; the server holds to it too. */}
          {(!clientMode || m.status === 'DRAFT' || m.status === 'SCHEDULED') && (
            <button
              type="button"
              aria-label={`Delete message ${m.subject || m.id}`}
              onClick={(e) => {
                e.stopPropagation();
                void handleDelete(m);
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-2 pt-4 border-t border-gray-200" data-testid="messages-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5">
          <MessageSquare className="w-4 h-4 text-gray-400" />
          Guest Messages
        </h3>
        <button
          type="button"
          onClick={() => openCompose()}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Plus className="w-4 h-4" />
          New Message
        </button>
      </div>

      {!isLoading && messages.length === 0 ? (
        <p className="text-xs text-gray-500">
          No broadcasts yet — message all pending guests, share updates, or send day-before info.
        </p>
      ) : (
        <EntityList
          items={messages}
          columns={columns}
          getKey={(m) => m.id}
          isLoading={isLoading}
          onRowClick={(m) =>
            m.status === 'SENT' || (clientMode && m.channel === 'SMS') ? undefined : openCompose(m)
          }
        />
      )}

      <EntityModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Message' : 'New Guest Message'}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <span className="block text-sm font-medium text-gray-700">Send by</span>
            <div className="mt-1 flex gap-2">
              {CHANNEL_OPTIONS.filter(
                // Texting withdrawn: no new texts, but a text being edited keeps its channel.
                // The portal never texts.
                (option) => option.value !== 'SMS' || canText || (channel === 'SMS' && !clientMode),
              ).map((option) => {
                const Icon = option.icon;
                const active = channel === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setChannel(option.value)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${
                      active
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {option.label}
                  </button>
                );
              })}
            </div>
            {channel === 'SMS' && (
              <p className="mt-1 text-xs text-gray-500">
                Texts go to every phone number on each guest record. Keep it short — there is no
                subject line or formatting, and the RSVP link is appended to the text.
              </p>
            )}
          </div>
          {!isSmsDraft && (
            <div>
              <label htmlFor="message-subject" className="block text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                id="message-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Update: ceremony time"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
              />
            </div>
          )}
          <MessageComposer
            id="message-body"
            label="Message"
            value={body}
            onChange={setBody}
            rows={4}
            maxLength={10000}
            record={{ entityType: 'Event', entityId: eventId }}
            assistant={!clientMode}
            subject={isSmsDraft ? undefined : subject}
            onSubjectChange={isSmsDraft ? undefined : setSubject}
          />
          {isSmsDraft && <SmsLength body={body.trim()} />}
          <div>
            <span className="block text-sm font-medium text-gray-700">
              Send to (leave empty for everyone)
            </span>
            <div className="mt-1 flex flex-wrap gap-3">
              {RSVP_AUDIENCE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={rsvpStatuses.includes(option.value)}
                    onChange={() => toggleStatus(option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {sideOptions.length > 0 && (
              <div className="mt-2">
                <FilterSelect
                  label="Side"
                  multiple
                  items={sideOptions}
                  value={sides}
                  onChange={(v) => setSides(v as string[])}
                  getValue={(o) => o.value}
                  getLabel={(o) => o.label}
                />
              </div>
            )}
          </div>
        </div>
      </EntityModal>
    </div>
  );
}

/**
 * "212 characters · 2 texts per number" — what a text costs. The RSVP link is
 * added only for guests who hold an invitation, so when it tips the count
 * over, both counts are given.
 */
function SmsLength({ body }: { body: string }) {
  const alone = smsSegments(body);
  const withLink = smsSegments(body + rsvpLinkSample());
  const texts = (n: number) => `${n} text${n === 1 ? '' : 's'}`;
  const heavy = withLink.segments > 2;
  return (
    <p className="-mt-2 text-xs text-gray-500" aria-live="polite">
      {alone.characters} character{alone.characters === 1 ? '' : 's'} ·{' '}
      <span className={heavy ? 'font-medium text-amber-700' : undefined}>
        {withLink.segments === alone.segments
          ? `${texts(alone.segments)} per number, with or without the RSVP link`
          : `${texts(Math.max(alone.segments, 1))} per number, ${texts(withLink.segments)} for guests sent their RSVP link`}
      </span>
      {withLink.encoding === 'UCS2' &&
        ' — emoji, Hebrew or curly quotes make each text hold 70 characters, not 160'}
    </p>
  );
}
